"use no memo";

import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/browser";
import { Button, Heading } from "@stacklet/ui";
import { useForm } from "react-hook-form";
import { useLazyLoadQuery, useMutation } from "react-relay";
import { generatePath, useNavigate } from "react-router";
import { graphql } from "relay-runtime";

import { IdpManagedNotice } from "app/components/rbac/IdpManagedNotice";
import { paths } from "app/constants/paths";
import { useAlertContext } from "app/contexts";
import { useHasScopePermission } from "app/hooks";
import { useRouteParams } from "app/hooks/routing/useRouteParams";
import { UpdateRoleAssignmentMutation } from "app/mutations/UpdateRoleAssignmentMutation";
import { UpdateUserGroupMutation } from "app/mutations/UpdateUserGroupMutation";
import { invalidateConnections } from "app/utils/relay";
import { isValidUuid, NIL_UUID } from "app/utils/uuid";
import { NotFound } from "app/views/NotFound";
import { RelayEnvironment } from "RelayEnvironment";

import { AccessControlSettingsPage } from "../AccessControlSettingsPage";
import { GroupMembershipTransferList } from "../GroupUsers/GroupMembershipTransferList";
import { GroupActionButtons } from "./GroupActionButtons";
import { GroupFormFields } from "./GroupFormFields";
import { GroupFormLayout } from "./GroupFormLayout";
import {
  getDefaultGroupValues,
  groupSchema,
  getDefaultRole,
  type RoleFormValues,
} from "./GroupSchema";
import { buildRoleAssignments, handleRoleAssignmentErrors } from "./GroupUtils";

import type {
  EditGroupRootQuery,
  EditGroupRootQuery$data,
} from "./__generated__/EditGroupRootQuery.graphql";
import type { UpdateRoleAssignmentMutation as UpdateRoleAssignmentMutationType } from "app/mutations/__generated__/UpdateRoleAssignmentMutation.graphql";
import type { UpdateUserGroupMutation as UpdateUserGroupMutationType } from "app/mutations/__generated__/UpdateUserGroupMutation.graphql";

type Group = NonNullable<EditGroupRootQuery$data["userGroup"]>;

type RoleAssignmentEdge = NonNullable<
  NonNullable<Group["roleAssignments"]["edges"]>[0]
>;
type RoleAssignmentNode = RoleAssignmentEdge["node"];
type RoleAssignmentTarget = RoleAssignmentNode["target"];

/**
 * EditGroupRoot component
 *
 * This component allows editing of an existing group:
 * 1. Fetches group data by name from the URL params
 * 2. Populates the form with existing values
 * 3. Handles update mutations for group info and role assignments
 */
export function EditGroupRoot() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { alertDispatch } = useAlertContext();
  const { groupUuid = "" } = useRouteParams(paths.groupEdit);
  const navigate = useNavigate();
  const hasPermission = useHasScopePermission();
  const canManageGroups = hasPermission("system", "EDIT");

  // Guard against malformed UUIDs, which would otherwise fail GraphQL
  // variable coercion and error the whole query (see GroupDetailsRoot).
  const validUuid = isValidUuid(groupUuid);

  // Fetch data needed for form fields
  const data = useLazyLoadQuery<EditGroupRootQuery>(
    graphql`
      query EditGroupRootQuery(
        $groupUuid: UUID!
        $skip: Boolean!
        $accountGroupFilterElement: FilterElementInput
        $policyCollectionFilterElement: FilterElementInput
      ) {
        ...AccessControlSettingsPage_query
        ...GroupFormFields_query @skip(if: $skip) @alias(as: "groupFormFields")
        userGroup(uuid: $groupUuid) @skip(if: $skip) {
          displayName
          id
          name
          roleAssignmentPrincipal
          roleAssignments(first: 100) {
            edges {
              node {
                id
                role {
                  name
                }
                target {
                  __typename
                  roleAssignmentTarget
                  ... on RoleScope {
                    displayName
                  }
                  ... on AccountGroup {
                    name
                  }
                  ... on PolicyCollection {
                    name
                  }
                  ... on RepositoryConfig {
                    name
                  }
                }
              }
            }
          }
          source {
            __typename
          }
          userPermissions
          uuid
        }
      }
    `,
    {
      groupUuid: validUuid ? groupUuid : NIL_UUID,
      skip: !validUuid,
      accountGroupFilterElement: null,
      policyCollectionFilterElement: null,
    },
  );

  const group = data.userGroup;
  // Both are gated on the same `$skip`, so they are present or absent together.
  const groupFormFieldsRef = data.groupFormFields;

  // Helper function to get the friendly name from a target object for labels
  const getFriendlyName = (target: RoleAssignmentTarget): string => {
    if (!target) return "";
    switch (target.__typename) {
      case "RoleScope":
        return target.displayName || target.roleAssignmentTarget;
      case "AccountGroup":
      case "PolicyCollection":
      case "RepositoryConfig":
        return target.name || target.roleAssignmentTarget;
      default:
        // Fallback to the target ID if type or name is missing
        return target.roleAssignmentTarget;
    }
  };

  // helper function to create role objects
  const mapRolesToFormValues = (
    roleAssignments: RoleAssignmentNode[],
  ): RoleFormValues[] => {
    // Group roles by roleName
    const roleMap = new Map<
      string,
      { target: string; targetObject: RoleAssignmentTarget }[]
    >();

    roleAssignments.forEach((assignment) => {
      const roleName = assignment.role.name;
      const target = assignment.target.roleAssignmentTarget;

      if (!roleMap.has(roleName)) {
        roleMap.set(roleName, []);
      }
      const entries = roleMap.get(roleName);
      if (entries) {
        entries.push({
          target,
          targetObject: assignment.target,
        });
      }
    });

    // Convert to form structure
    const formRoles: RoleFormValues[] = [];

    roleMap.forEach((targetEntries, roleName) => {
      const targets = targetEntries.map((entry) => entry.target);
      const hasSystemAll = targets.includes("system:all");
      const hasAccountGroupAll = targets.includes("account-group:all");

      // Basic role scaffolding
      const role: RoleFormValues = {
        role: roleName,
        scope: hasSystemAll
          ? "GRANT_SYSTEM_ALL"
          : hasAccountGroupAll &&
              (roleName === "resource-viewer" || roleName === "policy-deployer")
            ? "GRANT_ACCOUNTS_ALL"
            : "TARGETS_SELECT",
      };

      // Set specific options based on role type
      if (roleName === "resource-viewer" && role.scope === "TARGETS_SELECT") {
        // Extract account group IDs and names from targets
        const accountGroups = targetEntries
          .filter(
            (entry) =>
              entry.target.startsWith("account-group:") &&
              entry.target !== "account-group:all",
          )
          .map((entry) => {
            const id = entry.target.replace("account-group:", "");
            return {
              value: id,
              label: getFriendlyName(entry.targetObject),
              roleAssignmentTarget: entry.target,
            };
          });

        role.resourceViewerOptions = {
          accountGroups,
        };
      } else if (
        roleName === "policy-deployer" &&
        role.scope === "TARGETS_SELECT"
      ) {
        // Extract account group IDs and names from targets
        const accountGroups = targetEntries
          .filter(
            (entry) =>
              entry.target.startsWith("account-group:") &&
              entry.target !== "account-group:all",
          )
          .map((entry) => {
            const id = entry.target.replace("account-group:", "");
            return {
              value: id,
              label: getFriendlyName(entry.targetObject),
              roleAssignmentTarget: entry.target,
            };
          });

        role.policyDeployerOptions = {
          accountGroups,
        };
      } else if (roleName === "creator" && role.scope === "TARGETS_SELECT") {
        role.creatorOptions = {
          accountGroups: targets.some((t) => t === "account-group:all"),
          policyCollections: targets.some((t) => t === "policy-collection:all"),
          policyRepositories: targets.some((t) => t === "repository:all"),
        };
      } else if (
        (roleName === "editor" || roleName === "owner") &&
        role.scope === "TARGETS_SELECT"
      ) {
        // Extract items for each target type with friendly names
        const accountGroups = targetEntries
          .filter(
            (entry) =>
              entry.target.startsWith("account-group:") &&
              entry.target !== "account-group:all",
          )
          .map((entry) => {
            const id = entry.target.replace("account-group:", "");
            return {
              value: id,
              label: getFriendlyName(entry.targetObject),
              roleAssignmentTarget: entry.target,
            };
          });

        const policyCollections = targetEntries
          .filter((entry) => entry.target.startsWith("policy-collection:"))
          .map((entry) => {
            const id = entry.target.replace("policy-collection:", "");
            return {
              value: id,
              label: getFriendlyName(entry.targetObject),
              roleAssignmentTarget: entry.target,
            };
          });

        const policyRepositories = targetEntries
          .filter((entry) => entry.target.startsWith("repository:"))
          .map((entry) => {
            const id = entry.target.replace("repository:", "");
            return {
              value: id,
              label: getFriendlyName(entry.targetObject),
              roleAssignmentTarget: entry.target,
            };
          });

        if (roleName === "editor") {
          role.editorOptions = {
            accountGroups,
            policyCollections,
            policyRepositories,
          };
        } else if (roleName === "owner") {
          role.ownerOptions = {
            accountGroups,
            policyCollections,
            policyRepositories,
          };
        }
      }

      formRoles.push(role);
    });

    return formRoles;
  };

  // Get form values based on group data
  const getFormValues = () => {
    if (!group) {
      return getDefaultGroupValues();
    }

    // Process role assignments if they exist
    let formRoles: RoleFormValues[] = [];
    if (group.roleAssignments?.edges) {
      const roleAssignments = group.roleAssignments.edges.map(
        (edge: RoleAssignmentEdge) => ({
          id: edge.node.id,
          role: edge.node.role,
          target: edge.node.target,
        }),
      );
      formRoles = mapRolesToFormValues(roleAssignments);
    }

    return {
      name: group.name,
      displayName: group.displayName || "",
      roles:
        formRoles.length > 0
          ? formRoles.map((role) => {
              const defaultRole = getDefaultRole();
              return {
                ...defaultRole,
                role: role.role,
                scope: role.scope,
                resourceViewerOptions:
                  role.resourceViewerOptions ||
                  defaultRole.resourceViewerOptions,
                policyDeployerOptions:
                  role.policyDeployerOptions ||
                  defaultRole.policyDeployerOptions,
                creatorOptions:
                  role.creatorOptions || defaultRole.creatorOptions,
                editorOptions: role.editorOptions || defaultRole.editorOptions,
                ownerOptions: role.ownerOptions || defaultRole.ownerOptions,
              };
            })
          : getDefaultGroupValues().roles,
    };
  };

  // Initialize form with values from the group
  const form = useForm({
    resolver: zodResolver(groupSchema),
    defaultValues: getFormValues(),
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = form;

  const [updateUserGroup] = useMutation<UpdateUserGroupMutationType>(
    UpdateUserGroupMutation,
  );

  const [updateRoleAssignment] = useMutation<UpdateRoleAssignmentMutationType>(
    UpdateRoleAssignmentMutation,
  );

  if (!groupUuid) {
    return <NotFound />;
  }

  const onSubmit = handleSubmit((formData) => {
    if (!canManageGroups) {
      alertDispatch({
        message: "You don't have permission to edit groups",
        severity: "error",
      });
      return;
    }

    if (!group) {
      alertDispatch({
        message: "Failed to update group: Group not found",
        severity: "error",
      });
      return;
    }

    // Save stays enabled on a pristine form so it can close a page whose only
    // changes were to membership, which commits itself. There is nothing to
    // commit here in that case, so just leave: submitting would rewrite the
    // group with its own values and re-grant its existing roles, leaving an
    // audit trail for an edit that never happened.
    if (!isDirty) {
      navigate(generatePath(paths.groupDetails, { groupUuid }));
      return;
    }

    setIsSubmitting(true);

    // Commit the role grants/revokes for the group. Split out so it can run on
    // its own for SSO-provisioned groups (whose name/displayName are owned by
    // the IdP and rejected by updateUserGroup) or after updateUserGroup for
    // directly-created groups.
    const commitRoleAssignments = () => {
      // Use the principal loaded with the current assignments (not one echoed
      // back by updateUserGroup) so grants and revokes both target the group
      // that actually owns the loaded assignments. The principal is derived
      // from the group's immutable id, so a rename never changes it — but
      // sourcing it here keeps that guarantee local rather than trusting the
      // mutation response.
      const principal = group.roleAssignmentPrincipal;
      // Pass initial role assignments to properly handle revocations
      const initialRoleAssignments = group.roleAssignments?.edges;
      const roleAssignments = buildRoleAssignments(
        formData.roles,
        principal,
        initialRoleAssignments,
      );

      updateRoleAssignment({
        variables: {
          input: roleAssignments,
          connections: [],
        },
        updater: (store) => {
          // invalidate access control grid connection
          invalidateConnections(
            RelayEnvironment,
            "GroupsTable_userGroups",
            "client:root",
            store,
          );

          // Granted assignments come back as standalone RoleAssignment records
          // but aren't appended to this group's cached roleAssignments
          // connection, so the details page (which reads that same connection
          // from its own query) shows stale roles until a hard refresh.
          // Invalidate the group record to force its details query to refetch
          // on next render.
          store.get(group.id)?.invalidateRecord();

          // Grant operations are handled by the mutation response
          const revokeOperations = roleAssignments.revoke || [];

          if (revokeOperations.length > 0 && group?.id) {
            const groupRecord = store.get(group.id);
            if (groupRecord) {
              // Get the roleAssignments connection
              const roleAssignmentsField = groupRecord.getLinkedRecord(
                "roleAssignments",
                {
                  first: 100,
                },
              );
              if (roleAssignmentsField) {
                const edges =
                  roleAssignmentsField.getLinkedRecords("edges") || [];

                // Filter out the revoked role assignments
                const remainingEdges = edges.filter((edge) => {
                  if (!edge) return true;

                  const node = edge.getLinkedRecord("node");
                  if (!node) return true;

                  const role = node.getLinkedRecord("role");
                  const target = node.getLinkedRecord("target");

                  if (!role || !target) return true;

                  const roleName = role.getValue("name");
                  const targetValue = target.getValue("roleAssignmentTarget");

                  // Check if this role assignment was revoked
                  const wasRevoked = revokeOperations.some(
                    (revokeOp) =>
                      revokeOp.roleName === roleName &&
                      revokeOp.target === targetValue &&
                      revokeOp.principal === group.roleAssignmentPrincipal,
                  );

                  return !wasRevoked;
                });

                // Update the edges with the filtered list
                roleAssignmentsField.setLinkedRecords(remainingEdges, "edges");
              }
            }
          }
        },
        onCompleted(roleResponse, roleErrors) {
          setIsSubmitting(false);
          if (handleRoleAssignmentErrors(roleErrors, alertDispatch)) {
            return;
          }

          alertDispatch({
            message: "group and roles updated successfully",
            severity: "success",
          });

          // Navigate back to the group details page after successful update.
          // Use the stable uuid so a rename still lands on the right page.
          navigate(generatePath(paths.groupDetails, { groupUuid }));
        },
        onError(error) {
          Sentry.captureException(error);
          setIsSubmitting(false);
          alertDispatch({
            message: `Failed to assign roles: ${error.message}`,
            severity: "error",
          });
        },
      });
    };

    // SSO-provisioned groups (any non-null source) are owned by the IdP;
    // updateUserGroup rejects them and their name/displayName are read-only in
    // the form anyway. Update only the role assignments.
    if (group.source) {
      commitRoleAssignments();
      return;
    }

    updateUserGroup({
      variables: {
        input: {
          id: group.id,
          name: formData.name,
          displayName: formData.displayName || undefined,
        },
      },
      onCompleted(response, errors) {
        if (errors) {
          errors.forEach((error) => {
            Sentry.captureException(error);
          });
          alertDispatch({
            message: `Failed to update group: ${errors[0].message}`,
            severity: "error",
          });
          setIsSubmitting(false);
          return;
        }

        const updatedGroup = response.updateUserGroup.userGroup;
        if (!updatedGroup) {
          setIsSubmitting(false);
          alertDispatch({
            message: "Failed to update group: No group updated",
            severity: "error",
          });
          return;
        }

        commitRoleAssignments();
      },
      onError(error) {
        Sentry.captureException(error);
        setIsSubmitting(false);
        alertDispatch({
          message: `Failed to update group: ${error.message}`,
          severity: "error",
        });
      },
    });
  });

  const handleCancel = () => {
    navigate(
      group ? generatePath(paths.groupDetails, { groupUuid }) : paths.groups,
    );
  };

  if (!group || !groupFormFieldsRef) {
    return (
      <AccessControlSettingsPage pageTitle="Edit Group" queryRef={data}>
        <div className="p-lg">
          <div className="mb-md flex justify-between">
            <Heading level={2}>Group Not Found</Heading>
          </div>

          <p className="text-body-medium text-text-primary">
            The requested group could not be found. It may have been deleted.
          </p>
          <div className="mt-lg">
            <Button onPress={handleCancel} variant="tertiary">
              Back to Groups
            </Button>
          </div>
        </div>
      </AccessControlSettingsPage>
    );
  }

  return (
    <AccessControlSettingsPage
      additionalCrumbs={[
        // The group's details page is the trail's parent level.
        {
          label: group.displayName || group.name,
          target: generatePath(paths.groupDetails, { groupUuid }),
        },
      ]}
      pageTitle="Edit Group"
      queryRef={data}
    >
      <GroupFormLayout
        form={form}
        isHelpOpen={isHelpOpen}
        onHelpToggle={setIsHelpOpen}
        onSubmit={onSubmit}
        title="Edit Group"
      >
        <GroupFormFields
          actionButtons={
            <GroupActionButtons
              canManageGroups={canManageGroups}
              form={form}
              isSubmitting={isSubmitting}
              onCancel={handleCancel}
              // Restore the loaded group (the form's defaultValues); the
              // fallback in GroupActionButtons resets to blank add-form values.
              onReset={() => reset()}
              isEdit
            />
          }
          membershipSection={
            // Membership of a synced group is owned by the identity provider
            // and the API rejects manual changes, so explain that instead of
            // hiding the section.
            group.source ? (
              <IdpManagedNotice title="Users">
                Users will automatically join this group on login when using an
                external source (e.g. SSO)
              </IdpManagedNotice>
            ) : (
              <GroupMembershipTransferList
                canEditMembership={group.userPermissions.includes("EDIT")}
                groupName={group.name}
                groupUuid={group.uuid}
              />
            )
          }
          metadataReadOnly={Boolean(group.source)}
          queryRef={groupFormFieldsRef}
        />
      </GroupFormLayout>
    </AccessControlSettingsPage>
  );
}
